import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronRight, Copy, Eye, MoreHorizontal, Pencil, Send, Trash2 } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { RecordQueue, useReportV2Store } from '@/frontend/store/ReportV2Store';
import { draftInputs, overlay } from '@/frontend/store/recordDrafts';
import { ReportAssetsProvider } from '@/frontend/reports/primitives';
import { useReportModule } from '@/frontend/reports/useReportModule';
import { InputSheet, SweepCellChange, findInput, inputsContext, sheetProgress } from '@/reports/inputs';
import { ReportEvaluation, ReportValues, asInputsReport } from '@/reports/types';
import { LoadingSpinner } from '../LoadingSpinner';
import { VerdictBadge } from '../runReports/ReportBadges';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { FillContextProvider, focusSlot } from './fillContext';
import { BeforePublishCard, ProgressCard, PublishBlocker } from './ProgressRail';
import { SheetForm } from './SheetForm';
import { NewReportV2Dialog } from './ReportV2ListPage';

export type SaveState = 'saved' | 'saving' | 'slow' | 'unsaved' | 'failed' | 'held' | 'rejected';

/** How the record's queue stands, for the page's save status: a refusal for good first, then what is held back, then the save under way. */
export const queueState = (queue: RecordQueue | undefined): SaveState => {
  if (!queue) return 'saved';
  if (queue.rejected) return 'rejected';
  if (queue.held) return 'held';
  if (queue.inFlight) return queue.slow ? 'slow' : 'saving';
  if (queue.pending) return queue.failed ? 'failed' : 'unsaved';
  return 'saved';
};

/** The inputs held back on the record, by their labels on `sheet`. */
export const heldLabels = (queue: RecordQueue | undefined, sheet: InputSheet) => draftInputs(queue?.held ?? null).map((inputId) => findInput(sheet, inputId)?.label ?? inputId);

/** "saved 2 min ago", "Saving...", that it failed and is being tried again, what is held back, or why it was refused (with a way to discard it). */
export function SaveStatus({ queue, sheet, since, onDiscard }: { queue: RecordQueue | undefined; sheet: InputSheet; since: string; onDiscard?: () => void }) {
  const state = queueState(queue);
  const alarm = (text: string) => <span className="text-destructive">{text}</span>;
  if (state === 'rejected') {
    return (
      <>
        {alarm(I18n.get('rv2SaveRejected', { message: queue?.rejected ?? '' }))}
        {/* not while what it would discard is being sent again: a refusal would bring it back */}
        {onDiscard && !queue?.inFlight && <button type="button" onClick={onDiscard} className="ml-1 underline underline-offset-2 hover:text-foreground">{I18n.get('rv2Discard')}</button>}
      </>
    );
  }
  if (state === 'held') return alarm(I18n.get('rv2OutOfDate', { inputs: heldLabels(queue, sheet).join(', ') }));
  if (state === 'failed') return alarm(I18n.get('rv2SaveFailed'));
  if (state === 'slow') return alarm(I18n.get('rv2SaveSlow'));
  return <>{state === 'saving' ? I18n.get('saving') : state === 'unsaved' ? I18n.get('unsavedChanges') : I18n.get('rv2Saved', { when: since })}</>;
}

/** "just now", "2 min ago", or a time: when the report was last saved. */
export function useSince(at: Date | string | null | undefined): string {
  const [, tick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => tick((n) => n + 1), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  if (!at) return '';
  const seconds = Math.max(0, (Date.now() - new Date(at).getTime()) / 1000);
  if (seconds < 45) return I18n.get('rv2JustNow');
  if (seconds < 3600) return I18n.get('rv2MinutesAgo', { count: String(Math.round(seconds / 60)) });
  return new Date(at).toLocaleString();
}

/** The later of two times; either may be missing. */
export const later = (a: Date | string | null | undefined, b: Date | string | null | undefined) => (!a || (b && new Date(b) > new Date(a)) ? b : a);

/**
 * Filling in a report from inputs, with no procedure run. What is entered is
 * the report's record — on the sheet of the schema version the record is
 * entered on — and any other report on the same record sees it too. The
 * sheet's sections down the middle; down the left, how far along it is and
 * what publishing still waits for. Every change saves itself through the
 * store's queue for the record (`edit`): only the inputs changed, and of a
 * sweep only the cell inputs, so another page on the same record keeps its
 * own; a table is saved whole. The form is what is stored with what is typed
 * on the record and not saved yet over it, so every report on the record
 * shows the same, whichever it was typed into.
 */
export function ReportV2FillPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const state = useReportV2Store((s) => s.states[id]);
  // what is typed on the record and not saved yet, through this report or any other on it
  const queue = useReportV2Store((s) => (s.states[id] ? s.queues[s.states[id].record.id] : undefined));
  const missing = useReportV2Store((s) => !!s.missing[id]);
  const fetchState = useReportV2Store((s) => s.fetchState);
  const save = useReportV2Store((s) => s.save);
  const edit = useReportV2Store((s) => s.edit);
  const flushRecord = useReportV2Store((s) => s.flushRecord);
  const discard = useReportV2Store((s) => s.discard);
  const upload = useReportV2Store((s) => s.upload);
  const fileUrls = useReportV2Store((s) => s.fileUrls);
  const publish = useReportV2Store((s) => s.publish);
  const remove = useReportV2Store((s) => s.remove);

  const [showPublish, setShowPublish] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showAnother, setShowAnother] = useState(false);
  const [editingTitle, setEditingTitle] = useState<string | null>(null);

  // what was cached of the record may be older than what is stored (another report on it saved since)
  useEffect(() => { void fetchState(id); }, [id, fetchState]);
  // leaving the page, or moving on to another report, saves what is typed on its record now rather than in a moment
  useEffect(() => () => {
    const recordId = useReportV2Store.getState().states[id]?.record.id;
    if (recordId && useReportV2Store.getState().queues[recordId]?.pending) void flushRecord(recordId);
  }, [id, flushRecord]);

  // the record as stored, with what is typed on it over that: held back, being saved, then waiting to be
  const inputs = useMemo(() => (state ? overlay(state.sheet, state.record.values, [queue?.held ?? null, queue?.inFlight ?? null, queue?.pending ?? null]) : null), [state, queue]);
  const change = (inputId: string, value: unknown, cell?: SweepCellChange) => edit(id, { inputId, value, cell });

  const { module } = useReportModule(state ? { key: state.report.reportKey, bundleVersionId: state.bundle.versionId } : null);
  const definition = module?.definition ? asInputsReport(module.definition) : null;
  const progress = useMemo(() => (state && inputs ? sheetProgress(state.sheet, inputs) : null), [state?.sheet, inputs]);
  // a report on an earlier version of the record's schema may require inputs the record's own sheet leaves optional: publishing waits for those too
  const reportMissing = useMemo(() => (state && inputs && state.reportSheet && state.reportSheet !== state.sheet ? sheetProgress(state.reportSheet, inputs).missing : []), [state?.reportSheet, state?.sheet, inputs]);
  const since = useSince(later(state?.report.updatedAt, state?.record.updatedAt));
  const recordId = state?.record.id ?? '';
  const resolveFiles = useCallback((ids: string[]) => fileUrls(recordId, ids), [fileUrls, recordId]);
  const fill = useMemo(() => ({
    editable: true,
    upload: (file: File, onProgress?: (p: number) => void) => upload(recordId, file, onProgress),
    traces: state?.context.traces ?? {},
  }), [recordId, upload, state?.context.traces]);

  if (missing && !state) {
    return <div data-slot="reports-v2" className="p-[1.5rem] text-sm text-muted-foreground">{I18n.get('modelNotFound', { model: I18n.get('reportV2') })}</div>;
  }
  if (!state || !inputs || !progress) {
    return <div className="flex justify-center py-10" aria-label={I18n.get('loading')}><LoadingSpinner /></div>;
  }

  const { report, bundle, schema, sheet, versions, alsoOnRecord } = state;
  // what is held back on this record, by label
  const saveState = queueState(queue);
  const latest = versions[0] ?? null;
  const values: ReportValues = definition ? definition.normalizeValues(report.values) : report.values;
  const unsigned = (definition?.signoffs ?? []).filter((s) => !s.optional && !String((values.signoffs as Record<string, unknown> | undefined)?.[s.id] ?? '').trim());
  const slotKey = (slot: (typeof progress.missing)[number]) => `${slot.input}:${slot.cell ?? ''}:${slot.field ?? ''}`;
  const formMissing = new Set(progress.missing.map(slotKey));
  const missingInputs = [...progress.missing, ...reportMissing.filter((slot) => !formMissing.has(slotKey(slot)))];
  const blockers: PublishBlocker[] = [
    ...missingInputs.map((slot) => ({
      key: slotKey(slot),
      label: slot.cell === undefined ? I18n.get('rv2IsRequired', { label: slot.label }) : I18n.get('rv2IsEmpty', { label: slot.label }),
      blocking: true,
      onSelect: () => focusSlot(slot),
    })),
    ...unsigned.map((s) => ({ key: `signoff:${s.id}`, label: I18n.get('rv2Signature', { label: s.label }), blocking: false, onSelect: () => openPreview() })),
  ];

  const openPreview = () => {
    void flushRecord(state.record.id);
    void navigate(`/reports-v2/${id}/preview`);
  };

  const saveTitle = async () => {
    const title = editingTitle?.trim();
    setEditingTitle(null);
    if (title && title !== report.title) await save(id, { title });
  };

  let verdict: ReportEvaluation | null = null;
  if (definition && showPublish) {
    try {
      // what publishing judges: the record as stored, once what is typed has saved
      verdict = definition.evaluate(values, { ...inputsContext(state.reportSheet ?? sheet, state.record.values, state.context.traces, { id: report.id, title: report.title }), settings: state.context.settings });
    } catch {
      verdict = null;
    }
  }

  return (
    // the page fills the window and never widens it, as the studio does: what doesn't fit wraps or scrolls in its own box
    <div data-slot="reports-v2" className="min-h-svh min-w-0 bg-muted/40 text-sm text-foreground" style={{ contain: 'inline-size' }}>
      <header className="border-b-[1px] bg-background px-[1.5rem] pt-[0.75rem] pb-[1rem]">
        <nav aria-label={I18n.get('breadcrumb')} className="mb-1 flex items-center gap-1 text-xs text-muted-foreground">
          <Link to="/reports-v2" className="hover:text-foreground">{I18n.get('rv2Reports')}</Link>
          <ChevronRight className="size-3" />
          <span className="text-sky-800">{report.title}</span>
        </nav>
        <div className="flex flex-wrap items-start justify-between gap-[0.75rem]">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {editingTitle !== null ? (
                <Input autoFocus aria-label={I18n.get('rv2Title')} value={editingTitle} onChange={(e) => setEditingTitle(e.target.value)} onBlur={() => void saveTitle()}
                  onKeyDown={(e) => { if (e.key === 'Enter') void saveTitle(); if (e.key === 'Escape') setEditingTitle(null); }} className="h-9 w-[28rem] max-w-full text-lg font-semibold" />
              ) : (
                // wraps rather than truncating: text that can't wrap widens the app shell's content column, and the page with it
                <h1 className="group min-w-0 text-xl font-semibold [overflow-wrap:anywhere]">
                  {report.title}{' '}
                  <button type="button" aria-label={I18n.get('rv2Rename')} onClick={() => setEditingTitle(report.title)} className="inline-flex size-6 items-center justify-center rounded align-middle text-muted-foreground hover:bg-muted hover:text-foreground"><Pencil className="size-3.5" /></button>
                </h1>
              )}
              <Badge variant="outline" className="gap-1 border-sky-200 bg-sky-50 text-sky-800"><Pencil className="size-3" /> {I18n.get('rv2ManualEntry')}</Badge>
              {latest ? <Link to={`/reports-v2/${id}/versions/${latest.id}`}><VerdictBadge verdict={latest.verdict} label={I18n.get('rv2PublishedVersion', { number: String(latest.number), verdict: latest.verdictLabel })} /></Link> : <Badge variant="outline">{I18n.get('rv2Draft')}</Badge>}
            </div>
            <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
              <span>{I18n.get('rv2ReportBundle')}</span>
              <span className="font-medium text-foreground">{bundle.title}</span>
              <span>v{bundle.versionNumber} · {I18n.get('rv2OnSchema', { title: schema.title, number: String(schema.versionNumber) })} · {I18n.get('rv2NoProcedureRun')} ·</span>
              <span aria-live="polite">
                <SaveStatus queue={queue} sheet={sheet} since={since} onDiscard={() => discard(state.record.id)} />
              </span>
            </p>
            {alsoOnRecord.length > 0 && (
              <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                <span>{I18n.get('rv2AlsoOnRecord')}</span>
                {alsoOnRecord.map((r, i) => <span key={r.id}><Link to={`/reports-v2/${r.id}`} className="text-sky-800 underline-offset-2 hover:underline [overflow-wrap:anywhere]">{r.title}</Link>{i < alsoOnRecord.length - 1 ? ',' : ''}</span>)}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="outline" onClick={openPreview}><Eye /> {I18n.get('rv2PreviewReport')}</Button>
            <Button onClick={() => { void flushRecord(state.record.id); setShowPublish(true); }}><Send /> {I18n.get('publish')}</Button>
            <DropdownMenu>
              <DropdownMenuTrigger render={<Button variant="ghost" size="icon" aria-label={I18n.get('rv2MoreActions')} />}><MoreHorizontal /></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => { void flushRecord(state.record.id); setShowAnother(true); }}><Copy /> {I18n.get('rv2AnotherReportOnRecord')}</DropdownMenuItem>
                <DropdownMenuItem variant="destructive" onClick={() => setShowDelete(true)}><Trash2 /> {I18n.get('rv2DeleteReport')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <FillContextProvider value={fill}>
        <ReportAssetsProvider resolve={resolveFiles}>
          <div className="mx-auto grid max-w-[80rem] grid-cols-1 gap-[1.25rem] px-[1.5rem] py-[1.25rem] lg:grid-cols-[17rem_minmax(0,1fr)]">
            <aside className="flex flex-col gap-[1rem] lg:sticky lg:top-[1rem] lg:max-h-[calc(100svh-2rem)] lg:self-start lg:overflow-y-auto">
              <ProgressCard progress={progress} />
              <BeforePublishCard blockers={blockers} />
              <p className="px-1 text-xs text-muted-foreground">{I18n.get('rv2SheetNote')}</p>
            </aside>
            <main className="min-w-0">
              <SheetForm sheet={sheet} inputs={inputs} progress={progress.sections} onChange={change} />
            </main>
          </div>
        </ReportAssetsProvider>
      </FillContextProvider>

      <Dialog open={showPublish} onOpenChange={setShowPublish}>
        <PublishDialog
          blocking={missingInputs.length}
          // publishing waits for what is being saved, and is refused over what failed or is held back
          saving={saveState}
          held={heldLabels(queue, sheet)}
          rejected={queue?.rejected ?? null}
          onRetry={() => flushRecord(state.record.id)}
          verdict={verdict}
          onPublish={async (note) => {
            // saves what is typed first, and publishes nothing over what does not save
            const version = await publish(id, note);
            if (version) setShowPublish(false);
          }}
        />
      </Dialog>

      <Dialog open={showAnother} onOpenChange={setShowAnother}>
        {showAnother && <NewReportV2Dialog onRecord={{ recordId: report.recordId, schemaId: state.record.schemaId, title: report.title }} onDone={() => setShowAnother(false)} />}
      </Dialog>

      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{I18n.get('rv2DeleteReport')}</DialogTitle>
            <DialogDescription>{I18n.get(alsoOnRecord.length ? 'rv2DeleteConfirmShared' : 'rv2DeleteConfirm', { title: report.title, versions: String(versions.length) })}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDelete(false)}>{I18n.get('cancel')}</Button>
            <Button variant="destructive" onClick={() => void (async () => { if (await remove(id)) await navigate('/reports-v2'); })()}><Trash2 /> {I18n.get('rv2DeleteReport')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PublishDialog({ blocking, saving, held, rejected, onRetry, verdict, onPublish }: {
  blocking: number; saving: SaveState; held: string[]; rejected: string | null; onRetry: () => Promise<boolean>; verdict: ReportEvaluation | null; onPublish: (note: string) => Promise<void>;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const failed = saving === 'failed';
  // refused over what failed, was refused or is held back; it waits for a save under way
  const notSaved = held.length > 0 || !!rejected || failed;
  const waiting = saving === 'saving' || saving === 'slow' || saving === 'unsaved';
  const run = (work: () => Promise<unknown>) => void (async () => { setBusy(true); try { await work(); } finally { setBusy(false); } })();
  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{I18n.get('rv2PublishTitle')}</DialogTitle>
        <DialogDescription>
          {held.length ? I18n.get('rv2PublishHeldBack', { inputs: held.join(', ') })
            : rejected ? I18n.get('rv2PublishRejected', { message: rejected })
            : failed ? I18n.get('rv2PublishNotSaved')
            : blocking ? I18n.get('rv2PublishBlocked', { count: String(blocking) })
            : waiting ? I18n.get(saving === 'slow' ? 'rv2PublishWaitingSlow' : 'rv2PublishWaiting')
            : verdict ? I18n.get('rv2PublishComesTo') : I18n.get('rv2PublishFreezes')}
        </DialogDescription>
      </DialogHeader>
      {!blocking && !notSaved && !waiting && verdict && <div><VerdictBadge verdict={verdict.verdict} label={verdict.verdictLabel} /></div>}
      {!blocking && !notSaved && <Textarea aria-label={I18n.get('rv2PublishNote')} placeholder={I18n.get('rv2PublishNote')} value={note} onChange={(e) => setNote(e.target.value)} />}
      <DialogFooter>
        {failed && <Button variant="outline" disabled={busy} onClick={() => run(onRetry)}>{I18n.get('rv2TryAgain')}</Button>}
        <Button disabled={!!blocking || notSaved || busy} onClick={() => run(() => onPublish(note))}><Send /> {busy ? I18n.get('saving') : I18n.get('publish')}</Button>
      </DialogFooter>
    </DialogContent>
  );
}
